import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { teamsService, userService } from '../services/api';

const TeamContext = createContext();

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};

export const TeamProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      initializeTeamSelection();
    } else {
      setSelectedTeam(null);
      setAvailableTeams([]);
      setIsLoading(false);
    }
  }, [currentUser]);

  const initializeTeamSelection = async () => {
    try {
      setIsLoading(true);
      
      if (currentUser.role === 'SUPER_ADMIN') {
        // SuperAdmin peut voir toutes les équipes
        const allTeams = await teamsService.getAll();
        
        const teamsWithRole = allTeams.map(team => ({
          ...team,
          userRole: 'SUPER_ADMIN',
          isMainTeam: false,
          isDelegated: false
        }));
        
        setAvailableTeams(teamsWithRole);
        // Sélectionner la première équipe par défaut
        if (teamsWithRole.length > 0) {
          const defaultTeam = teamsWithRole[0];
          setSelectedTeam(defaultTeam);
          localStorage.setItem('selectedTeamId', defaultTeam.id.toString());
          
          // Déclencher l'événement teamChanged pour initialiser les données filtrées
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('teamChanged', { 
              detail: { 
                team: defaultTeam,
                userId: currentUser.id
              } 
            }));
          }, 10); // Délai minimal pour s'assurer que les contextes sont prêts
        }
      } else {
        // Admin/Viewer : équipe principale + délégations
        console.log('🔄 Chargement des équipes managées pour:', currentUser.email);
        const startTime = Date.now();
        const managedTeams = await teamsService.getManagedTeams(currentUser.id);
        const loadTime = Date.now() - startTime;
        console.log('✅ Équipes managées chargées en', loadTime, 'ms:', managedTeams.length, 'équipes');
        setAvailableTeams(managedTeams);
        
        // Sélectionner l'équipe principale par défaut
        const mainTeam = managedTeams.find(team => team.isMainTeam);
        let defaultTeam = null;
        if (mainTeam) {
          defaultTeam = mainTeam;
        } else if (managedTeams.length > 0) {
          defaultTeam = managedTeams[0];
        }
        
        if (defaultTeam) {
          setSelectedTeam(defaultTeam);
          localStorage.setItem('selectedTeamId', defaultTeam.id.toString());
          
          // Déclencher l'événement teamChanged pour initialiser les données filtrées
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('teamChanged', { 
              detail: { 
                team: defaultTeam,
                userId: currentUser.id
              } 
            }));
          }, 10); // Délai minimal pour s'assurer que les contextes sont prêts
        }
      }
    } catch (error) {
      console.error('Erreur initialisation équipes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const switchTeam = (team) => {
    console.log('🔄 Changement d\'équipe:', {
      from: selectedTeam?.name,
      to: team.name,
      userRole: team.userRole || 'Admin',
      isMainTeam: team.isMainTeam,
      isDelegated: team.isDelegated
    });
    
    setSelectedTeam(team);
    
    // Sauvegarder l'équipe sélectionnée dans localStorage
    localStorage.setItem('selectedTeamId', team.id.toString());
    
    // Émettre un événement personnalisé pour notifier les autres composants
    window.dispatchEvent(new CustomEvent('teamChanged', { 
      detail: { 
        team,
        userId: currentUser.id
      } 
    }));
  };

  const getTeamPermissions = () => {
    if (!selectedTeam) return {};
    
    if (currentUser.role === 'SUPER_ADMIN') {
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: true
      };
    }
    
    if (selectedTeam.isMainTeam) {
      // Admin principal ou adjoint : tous les droits
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: true
      };
    }
    
    if (selectedTeam.isDelegated && selectedTeam.permissions) {
      // Délégation : utiliser les permissions définies
      return selectedTeam.permissions;
    }
    
    // Par défaut : lecture seule
    return {
      canView: true,
      canAdd: false,
      canEdit: false,
      canDelete: false
    };
  };

  const value = {
    selectedTeam,
    availableTeams,
    isLoading,
    switchTeam,
    getTeamPermissions,
    refreshTeams: initializeTeamSelection
  };

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
};

export default TeamContext;